import React, { PureComponent } from 'react';
import { InputMask, format } from '@react-input/mask';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { v4 as UUID } from 'uuid';
import AbstractControl from '../AbstractControl';
import AutocompleteValue from '../../../common/helpers/AutocompleteValue';

const MASK_REPLACEMENT = {
  '#': /\d/,
};

const getDigitsOnly = value => {
  if (value === null || value === undefined) {
    return '';
  }

  return String(value).replace(/\D/g, '');
};

const formatWithMask = (value, mask) => {
  if (!mask) {
    return value === null || value === undefined ? '' : String(value);
  }

  const digits = getDigitsOnly(value);

  if (!digits) {
    return '';
  }

  return format(digits, {
    mask,
    replacement: MASK_REPLACEMENT,
  });
};

class MaskControl extends PureComponent {
  static propTypes = {
    id: PropTypes.string.isRequired,
    mask: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    style: PropTypes.object,
    focus: PropTypes.bool,
    lockedState: PropTypes.bool,
    disabledState: PropTypes.bool,
    required: PropTypes.bool,
    requiredBlue: PropTypes.bool,
    changeOnBlur: PropTypes.bool,
    hint: PropTypes.string,
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    errorText: PropTypes.string,
    className: PropTypes.string,
    parentClassName: PropTypes.string,
    tooltipText: PropTypes.string,
  };

  static defaultProps = {
    value: '',
  };

  constructor(props) {
    super(props);

    this.state = {
      value: props.value,
    };

    this.inputAutocompleteValue = AutocompleteValue();
    this.inputNameValue = 'input-' + UUID();

    this.handleOnChange = this.handleOnChange.bind(this);
    this.handleOnBlur = this.handleOnBlur.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (this.props.changeOnBlur && prevProps.value !== this.props.value && this.props.value !== this.state.value) {
      this.setState({ value: this.props.value });
    }
  }

  render() {
    const {
      // Props for current component
      id,
      mask,
      lockedState,
      disabledState,
      className,
      // Props for abstract component
      hint,
      style,
      focus,
      label,
      errorText,
      required,
      requiredBlue,
      tooltipText,
      parentClassName,
      changeOnBlur,
    } = this.props;

    const componentClassName = classNames('mask-input-component', className);

    const inputAbstractProps = {
      hint,
      style,
      focus,
      label,
      lockedState,
      disabledState,
      errorText,
      required,
      requiredBlue,
      tooltipText,
      parentClassName,
      className: componentClassName,
      labelFor: id,
    };

    const value = changeOnBlur ? this.state.value : this.props.value;
    const maskedValue = formatWithMask(value, mask);

    return (
      <AbstractControl {...inputAbstractProps}>
        <InputMask
          autoComplete={this.inputAutocompleteValue}
          name={this.inputNameValue}
          id={id}
          type="tel"
          mask={mask}
          replacement={MASK_REPLACEMENT}
          disabled={lockedState || disabledState}
          value={maskedValue}
          onBlur={this.handleOnBlur}
          onChange={this.handleOnChange}
        />
      </AbstractControl>
    );
  }

  handleOnChange(event) {
    let value = getDigitsOnly(event.target.value);

    if (this.props.changeOnBlur) {
      this.setState({ value: value });
    } else {
      this.props.onChange(value);
    }
  }

  handleOnBlur() {
    const value = this.props.changeOnBlur ? this.state.value : this.props.value;

    this.props.onChange(getDigitsOnly(value));
  }
}

export default MaskControl;
